<template>
  <div class="min-h-screen mt-[20px] relative overflow-hidden pb-[100px]">
    <!-- Background decorative elements - Optimized for mobile -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute -top-20 -right-16 sm:-right-32 w-48 sm:w-96 h-48 sm:h-96 bg-gradient-to-br from-primary-200/20 to-blue-300/10 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute top-1/3 -left-16 sm:-left-32 w-40 sm:w-80 h-40 sm:h-80 bg-gradient-to-tr from-indigo-200/20 to-purple-300/10 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute bottom-20 right-1/4 w-20 sm:w-40 h-20 sm:h-40 bg-gradient-to-br from-cyan-200/15 to-blue-300/10 rounded-full blur-2xl"
      ></div>
    </div>

    <div class="relative container mx-auto px-4 py-4 sm:py-8 pt-[70px] sm:pt-[90px]">
      <!-- Loading State -->
      <LoadingState v-if="loading" />

      <!-- Enhanced Error State - Mobile optimized -->
      <div v-else-if="error" class="max-w-full sm:max-w-md mx-auto">
        <div
          class="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200/50 rounded-2xl p-6 sm:p-8 shadow-xl shadow-red-100/50 backdrop-blur-sm"
        >
          <div class="text-center">
            <div
              class="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <i class="fa-solid fa-exclamation-triangle text-white text-lg sm:text-2xl"></i>
            </div>
            <h3 class="text-lg font-bold text-red-900 mb-2">Erreur de chargement</h3>
            <div class="text-red-700 mb-6 text-sm sm:text-base">{{ error }}</div>
            <button
              @click="retryLoad"
              class="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 text-sm sm:text-base"
            >
              <i class="fa-solid fa-refresh mr-2"></i>
              Réessayer
            </button>
          </div>
        </div>
      </div>

      <!-- Enhanced Statistics Cards - Mobile responsive -->
      <div
        v-if="isAuthenticated && !loading"
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12"
      >
        <div
          class="group relative overflow-hidden bg-white/80 backdrop-blur-xl border border-gray-200 rounded-2xl sm:rounded-3xl shadow shadow-gray-200/50 hover:shadow-lg transition-all duration-300 sm:hover:-translate-y-2"
        >
          <div class="relative p-4 sm:p-5">
            <div class="flex items-center">
              <div
                class="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg"
              >
                <i class="fa-solid fa-chart-line text-white text-sm"></i>
              </div>
              <div class="ml-4 sm:ml-6 flex-1">
                <p
                  class="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1"
                >
                  Tentatives totales
                </p>
                <p
                  class="text-xl sm:text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300"
                >
                  {{ stats.totalAttempts }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          class="group relative overflow-hidden bg-white/80 backdrop-blur-xl border border-gray-200 rounded-2xl sm:rounded-3xl shadow shadow-gray-200/50 hover:shadow-lg transition-all duration-300 sm:hover:-translate-y-2"
        >
          <div class="relative p-4 sm:p-5">
            <div class="flex items-center">
              <div
                class="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg"
              >
                <i class="fa-solid fa-trophy text-white text-sm sm:text-base"></i>
              </div>
              <div class="ml-4 sm:ml-6 flex-1">
                <p
                  class="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1"
                >
                  Score moyen
                </p>
                <p
                  class="text-xl sm:text-2xl font-bold text-gray-900 group-hover:text-orange-600 transition-colors duration-300"
                >
                  {{ Math.round(stats.averageScore) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          class="group relative sm:col-span-2 lg:col-span-1 overflow-hidden bg-white/80 backdrop-blur-xl border border-gray-200 rounded-2xl sm:rounded-3xl shadow shadow-gray-200/50 hover:shadow-lg transition-all duration-300 sm:hover:-translate-y-2"
        >
          <div class="relative p-4 sm:p-5">
            <div class="flex items-center">
              <div
                class="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg"
              >
                <i class="fa-solid fa-check-circle text-white text-sm"></i>
              </div>
              <div class="ml-4 sm:ml-6 flex-1">
                <p
                  class="text-xs sm:text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1"
                >
                  Exercices terminés
                </p>
                <p
                  class="text-xl sm:text-2xl font-bold text-gray-900 group-hover:text-green-600 transition-colors duration-300"
                >
                  {{ stats.completedExercices }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Enhanced Exam Types Section - Mobile optimized -->
      <div v-if="!loading" class="space-y-6 sm:space-y-8">
        <div v-for="examType in examTypes" :key="examType.slug" class="group">
          <div
            class="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-2xl sm:rounded-3xl shadow-xl shadow-gray-200/50 hover:shadow-2xl transition-all duration-500 overflow-hidden"
          >
            <!-- Enhanced Header - Mobile responsive -->
            <div
              class="relative py-4 sm:py-6 px-4 sm:px-8 bg-gradient-to-r from-gray-50/50 to-gray-100/50 border-b border-gray-200/50"
            >
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0"
              >
                <div class="flex-1 min-w-0">
                  <h2 class="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
                    {{ examType.name }}
                  </h2>
                  <p
                    v-if="examType.description"
                    class="text-sm sm:text-base text-gray-600 line-clamp-2"
                  >
                    {{ examType.description }}
                  </p>
                </div>
                <button
                  @click="toggleExamType(examType.slug)"
                  class="w-full sm:w-auto relative overflow-hidden cursor-pointer group/btn bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-all duration-300 text-sm sm:text-base"
                >
                  <span class="relative z-10 flex items-center justify-center">
                    <i
                      :class="
                        expandedExamTypes.has(examType.slug)
                          ? 'fa-solid fa-chevron-up'
                          : 'fa-solid fa-chevron-down'
                      "
                      class="mr-2 transition-transform duration-300"
                    ></i>
                    {{ expandedExamTypes.has(examType.slug) ? 'Masquer' : 'Découvrir' }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Expanded Content - Mobile optimized -->
            <div v-if="expandedExamTypes.has(examType.slug)" class="p-4 sm:p-8">
              <div
                v-if="loadingExercices.has(examType.slug)"
                class="flex justify-center py-8 sm:py-12"
              >
                <div class="relative">
                  <div
                    class="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-4 border-primary-200 border-t-primary-600"
                  ></div>
                </div>
              </div>

              <div v-else class="space-y-6 sm:space-y-8">
                <!-- Enhanced Exam Tabs - Mobile responsive -->
                <div class="relative">
                  <div class="flex flex-col md:flex-row flex-wrap gap-2 sm:gap-3">
                    <button
                      v-for="exam in getExamsByType(examType.slug)"
                      :key="exam.slug"
                      @click="setActiveExam(examType.slug, exam.slug)"
                      :class="[
                        'relative overflow-hidden px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl cursor-pointer font-semibold transition-all duration-300 text-sm sm:text-base flex-1 sm:flex-none',
                        activeExams.get(examType.slug) === exam.slug
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-xl shadow-primary-500/25'
                          : 'bg-white/70 text-gray-700 border border-gray-200 hover:bg-white hover:shadow-lg hover:border-primary-200',
                      ]"
                    >
                      <span class="relative z-10 flex items-center justify-center">
                        <i
                          :class="getExamIcon(exam.type)"
                          class="mr-1 sm:mr-2 text-xs sm:text-sm"
                        ></i>
                        <span class="truncate">{{ exam.name }}</span>
                      </span>
                    </button>
                  </div>
                </div>

                <!-- Enhanced Training Mode Button - Mobile responsive -->
                <div v-if="getActiveExam(examType.slug)" class="relative">
                  <div
                    class="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200/50 rounded-xl sm:rounded-2xl p-4 sm:p-6"
                  >
                    <div
                      class="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0"
                    >
                      <div class="flex items-center">
                        <div
                          class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0"
                        >
                          <i class="fa-solid fa-dumbbell text-white text-sm"></i>
                        </div>
                        <div class="min-w-0 flex-1">
                          <h4 class="text-base sm:text-lg font-bold text-gray-900 mb-1">
                            Mode Entraînement Intensif
                          </h4>
                          <p class="text-sm sm:text-base text-green-700 truncate">
                            {{ getActiveExam(examType.slug).name }}
                          </p>
                        </div>
                      </div>
                      <button
                        @click="startTrainingMode(getActiveExam(examType.slug).slug)"
                        class="w-full sm:w-auto relative cursor-pointer overflow-hidden group bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-4 sm:px-8 py-3 sm:py-4 rounded-xl font-bold transition-all duration-300 text-sm sm:text-base"
                      >
                        <span class="relative z-10 flex items-center justify-center">
                          <i class="fa-solid fa-play mr-2"></i>
                          Commencer l'entraînement
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Enhanced Exercices Section - Mobile responsive -->
                <div v-if="getActiveExam(examType.slug)" class="space-y-4 sm:space-y-6">
                  <div class="flex items-center justify-center sm:justify-end">
                    <div
                      class="flex items-center text-xs sm:text-sm text-gray-500 bg-gray-100 rounded-lg px-3 py-1"
                    >
                      <i class="fa-solid fa-info-circle mr-1"></i>
                      {{ getExercicesForActiveExam(examType.slug).length }} exercices disponibles
                    </div>
                  </div>

                  <div
                    v-if="getExercicesForActiveExam(examType.slug).length === 0"
                    class="text-center py-12 sm:py-16"
                  >
                    <div
                      class="w-16 h-16 sm:w-24 sm:h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-6"
                    >
                      <i class="fa-solid fa-inbox text-gray-400 text-2xl sm:text-4xl"></i>
                    </div>
                    <h4 class="text-lg sm:text-xl font-bold text-gray-500 mb-2">
                      Aucun exercice disponible
                    </h4>
                    <p class="text-sm sm:text-base text-gray-400">
                      Les exercices pour cet examen seront bientôt disponibles
                    </p>
                  </div>

                  <div v-else class="space-y-4 sm:space-y-6">
                    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                      <ExerciceCard
                        v-for="exercice in getExercicesForActiveExam(examType.slug).slice(0, 6)"
                        :key="exercice.slug"
                        :exercice="exercice"
                        @start="handleStartExercice"
                        @view-result="handleViewResult"
                        :lock-for-package="!havePackage(examType?.slug)"
                        class="transform hover:scale-105 transition-all duration-300"
                      />
                    </div>
                    <div class="text-center">
                      <button
                        @click="goToExercicePage(examType.slug)"
                        class="w-full sm:w-auto relative overflow-hidden group border border-primary-200 hover:border-primary-300 bg-white hover:bg-primary-50 text-gray-700 hover:text-primary-600 px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer text-sm sm:text-base"
                      >
                        <span class="relative z-10 flex items-center justify-center">
                          <i class="fa-solid fa-refresh mr-2"></i>
                          Voir tous les exercices
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Enhanced Recent Activity Section - Mobile responsive -->
      <div v-if="isAuthenticated && recentAttempts.length > 0 && !loading" class="mt-8 sm:mt-12">
        <div
          class="bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl sm:rounded-3xl shadow-xl shadow-gray-200/50 overflow-hidden"
        >
          <div
            class="relative px-4 sm:px-8 py-4 sm:py-6 bg-gradient-to-r from-gray-50/50 to-gray-100/50 border-b border-gray-200/50"
          >
            <div class="flex items-center">
              <div
                class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0"
              >
                <i class="fa-solid fa-clock-rotate-left text-white text-sm sm:text-lg"></i>
              </div>
              <div class="min-w-0 flex-1">
                <h3 class="text-lg sm:text-xl font-bold text-gray-900">Activité récente</h3>
                <p class="text-sm sm:text-base text-gray-600">
                  Vos dernières tentatives et résultats
                </p>
              </div>
            </div>
          </div>
          <div class="p-4 sm:p-8">
            <div class="space-y-3 sm:space-y-4">
              <div
                v-for="attempt in recentAttempts.slice(0, 5)"
                :key="attempt.slug"
                class="group relative overflow-hidden bg-gradient-to-r from-gray-50 to-gray-100/50 hover:from-white hover:to-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200/50 hover:border-primary-200 transition-all duration-300 cursor-pointer hover:shadow-lg"
                @click="handleViewResult(attempt)"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
                    <div
                      class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center group-hover:shadow-lg transition-shadow duration-300 flex-shrink-0"
                    >
                      <i class="fa-solid fa-file-alt text-white text-sm"></i>
                    </div>
                    <div class="min-w-0 flex-1">
                      <h4
                        class="text-sm sm:text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors duration-300 truncate"
                      >
                        {{ attempt.exercice?.title }}
                      </h4>
                      <p class="text-xs sm:text-sm text-gray-500 flex items-center mt-1">
                        <i class="fa-solid fa-calendar-alt mr-1"></i>
                        {{ formatDate(attempt.completed_at) }}
                      </p>
                    </div>
                  </div>
                  <div class="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
                    <div class="text-right">
                      <span
                        :class="[
                          'text-sm sm:text-lg font-bold px-2 sm:px-3 py-1 rounded-lg',
                          getScoreClass(attempt.score),
                        ]"
                      >
                        {{ attempt.score }}%
                      </span>
                    </div>
                    <i
                      class="fa-solid fa-chevron-right text-gray-400 group-hover:text-primary-500 transition-colors duration-300 text-sm"
                    ></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useLanguageStore } from '@/stores/language'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/composables/useToast'
import ExerciceCard from '@/components/exercices/ExerciceCard.vue'
import LoadingState from '@/components/exercices/LoadingState.vue'

const router = useRouter()
const store = useLanguageStore()
const authStore = useAuthStore()
const { success: showSuccess, error: showError } = useToast()

// State
const expandedExamTypes = ref(new Set())
const activeExams = ref(new Map())
const loadingExercices = ref(new Set())
const recentAttempts = ref([])
let unlocked_exercice_count = 0
const user_subscriptions = ref([])
const justExamLoader = ref(false)

// Computed
const examTypes = computed(() => store.examTypes)
const exams = computed(() => store.exams)
const exercices = computed(() => store.exercices)
const loading = computed(() => store.loading)
const error = computed(() => store.error)
const stats = computed(() => store.stats)
const isAuthenticated = computed(() => authStore.isAuthenticated)

// Methods
const getExamsByType = (examTypeSlug) => {
  return exams.value.filter((exam) => {
    const examType = examTypes.value.find((et) => et.slug === examTypeSlug)
    return examType && exam.exam_type === examType.id
  })
}

const getActiveExam = (examTypeSlug) => {
  const activeExamSlug = activeExams.value.get(examTypeSlug)
  return exams.value.find((exam) => exam.slug === activeExamSlug)
}

const getExercicesForActiveExam = (examTypeSlug) => {
  const activeExam = getActiveExam(examTypeSlug)
  if (!activeExam) return []

  return exercices.value
}

const getExamIcon = (type) => {
  const icons = {
    READING: 'fa-solid fa-book-open',
    LISTENING: 'fa-solid fa-headphones',
    WRITING: 'fa-solid fa-pen',
    SPEAKING: 'fa-solid fa-microphone',
  }
  return icons[type] || 'fa-solid fa-file-alt'
}

const getScoreClass = (score) => {
  if (score >= 80) return 'text-green-600'
  if (score >= 60) return 'text-yellow-600'
  return 'text-red-600'
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const toggleExamType = async (examTypeSlug) => {
  if (expandedExamTypes.value.has(examTypeSlug)) {
    expandedExamTypes.value.delete(examTypeSlug)
    // activeExams.value.delete(examTypeSlug)
  } else {
    expandedExamTypes.value.add(examTypeSlug)

    // Load exams and exercices for this exam type
    loadingExercices.value.add(examTypeSlug)

    try {
      const examTypeExams = getExamsByType(examTypeSlug)
      if (examTypeExams.length > 0) {
        // Set first exam as active by default
        activeExams.value.set(examTypeSlug, examTypeExams[0].slug)
        // Load exercices for the first exam
        justExamLoader.value = true
        await store.fetchExercicesByExam(examTypeExams[0].slug)
        justExamLoader.value = false
      }
    } catch (error) {
      showError('Erreur lors du chargement des exercices')
    } finally {
      loadingExercices.value.delete(examTypeSlug)
    }
  }
}

const setActiveExam = async (examTypeSlug, examSlug) => {

  if (activeExams.value.get(examTypeSlug) === examSlug) return

  unlocked_exercice_count = 0

  activeExams.value.set(examTypeSlug, examSlug)
  loadingExercices.value.add(examTypeSlug)

  try {
    await store.fetchExercicesByExam(examSlug)
  } catch (error) {
    showError('Erreur lors du chargement des exercices')
  } finally {
    loadingExercices.value.delete(examTypeSlug)
  }
}

const startTrainingMode = (examSlug) => {
  store.changePracticeType('training')
  router.push(`/exercice-training/${examSlug}`)
}

const handleStartExercice = (exercice) => {
  router.push(`/exercice/${exercice.slug}`)
}

const goToExercicePage = (examTypeSlug) => {
  const slug = activeExams.value.get(examTypeSlug)
  router.push(`/dashboard/exercises/${examTypeSlug}/${slug}`)
}

const handleViewResult = (attempt) => {
  router.push(`/result/${attempt.slug}`)
}

const havePackage = (exam_type_slug) => {
  if (user_subscriptions.value?.package?.exam_type === exam_type_slug) return true
  else {
    unlocked_exercice_count = unlocked_exercice_count + 1
    return unlocked_exercice_count <= 1
  }
}

const loadRecentAttempts = async () => {
  if (!isAuthenticated.value) return

  try {
    // const attempts = await store.getAllExerciceAttempts()
    recentAttempts.value = attempts
      .filter((attempt) => attempt.completed_at)
      .sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at))
  } catch (error) {
    console.error("Erreur lors du chargement de l'activité récente:", error)
  }
}

const retryLoad = async () => {
  store.clearError()
  await loadData()
}

const loadData = async () => {
  try {
    await Promise.all([store.fetchExamTypes(), store.fetchExams()])

    if (isAuthenticated.value) {
      await Promise.all([
        store.fetchUserStats(),
        // loadRecentAttempts()
      ])
    }
  } catch (error) {
    showError('Erreur lors du chargement des données')
  }
}

// Lifecycle
onMounted(async () => {
  const user_data = await authStore.fetchUser()
  user_subscriptions.value = user_data?.current_subscription
  await loadData()
  toggleExamType(examTypes.value[0]?.slug)
})
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
